/** Timestamped feedback (PRD §21): the playback time is captured when you start writing; tap to re-capture or clear. */
import { useState } from "react";
import type { FeedbackInput } from "../../api/client";
import { Button, Chip } from "../../components/ui";
import { clock, parseClock } from "../../lib/format";

const VERDICTS: { id: FeedbackInput["verdict"]; label: string }[] = [
  { id: "fix", label: "Fix" }, { id: "comment", label: "Comment" }, { id: "regenerate", label: "Regenerate" }, { id: "hold", label: "Hold" }, { id: "looks_good", label: "Looks good" },
];
const SCOPES: { id: NonNullable<FeedbackInput["scope"]>; label: string }[] = [
  { id: "version", label: "This version" }, { id: "episode_all_languages", label: "This episode, every language" },
  { id: "series", label: "The whole series" }, { id: "category", label: "All future content in this category" },
];

export function FeedbackComposer({ getTime, onCompose, onSubmit, busy }: {
  /** Current playback position in seconds, or null when there is no player. */
  getTime: () => number | null;
  /** Called when the owner starts writing (the screen pauses the video so the moment stays put). */
  onCompose?: () => void;
  onSubmit: (fb: FeedbackInput) => Promise<boolean>;
  busy?: boolean;
}) {
  const [note, setNote] = useState("");
  const [verdict, setVerdict] = useState<FeedbackInput["verdict"]>("fix");
  const [scope, setScope] = useState<NonNullable<FeedbackInput["scope"]>>("version");
  const [at, setAt] = useState<number | null>(null);
  const [cleared, setCleared] = useState(false);
  const [editingTime, setEditingTime] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const capture = () => { const t = getTime(); setAt(t == null ? null : Math.round(t * 10) / 10); setCleared(false); };
  const onFocus = () => { onCompose?.(); if (at == null && !cleared) capture(); };

  const submit = async () => {
    if (!note.trim() && verdict !== "looks_good") { setError("Write a short note so the Studio knows what to change."); return; }
    setError(null);
    const ok = await onSubmit({ verdict, at_seconds: at, note: note.trim() || undefined, scope });
    if (ok) { setNote(""); setAt(null); setCleared(false); setVerdict("fix"); setScope("version"); }
  };

  return (
    <form className="feedback-composer" onSubmit={(e) => { e.preventDefault(); void submit(); }} aria-label="Add feedback">
      <div className="chips" role="group" aria-label="Verdict">
        {VERDICTS.map((v) => <Chip key={v.id} pressed={verdict === v.id} onClick={() => setVerdict(v.id)}>{v.label}</Chip>)}
      </div>
      <div className="time-row">
        {editingTime ? (
          <input aria-label="Timestamp (m:ss)" className="time-input" autoFocus defaultValue={at == null ? "" : clock(at)} placeholder="m:ss"
            onBlur={(e) => { setAt(parseClock(e.target.value)); setCleared(parseClock(e.target.value) == null); setEditingTime(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }} />
        ) : at != null ? (
          <span className="time-chip">
            <button type="button" onClick={() => setEditingTime(true)} aria-label={`Timestamp ${clock(at)} — edit`}>at {clock(at)}</button>
            <button type="button" onClick={() => { setAt(null); setCleared(true); }} aria-label="Remove timestamp">×</button>
          </span>
        ) : <span className="muted small">No timestamp</span>}
        <Button size="sm" variant="ghost" icon="clock" onClick={capture}>Use current time</Button>
      </div>
      <label htmlFor="fb-note" className="sr-only">Note</label>
      <textarea id="fb-note" rows={2} value={note} onFocus={onFocus} onChange={(e) => setNote(e.target.value)} placeholder="What should change? e.g. Use $100/month instead." />
      <div className="feedback-foot">
        <label className="sr-only" htmlFor="fb-scope">Applies to</label>
        <select id="fb-scope" value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
          {SCOPES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <Button type="submit" variant="primary" busy={busy}>Add note</Button>
      </div>
      {scope === "category" && <p className="field-hint">This raises a learning proposal for the Studio's rules; you confirm it separately.</p>}
      {error && <p className="field-error" role="alert">{error}</p>}
    </form>
  );
}
